import React, { createContext, useContext, useEffect } from "react"
import { Link as GatsbyLink } from "gatsby"

export const DEFAULT_LANGUAGE = "fr"
export const SUPPORTED_LANGUAGES = ["fr", "en"]

const TranslationContext = createContext(undefined)

const translations = {
  fr: {
    home: "Accueil",
    "about us": "À propos",
    certification: "Certification",
    policies: "Politiques",
    contact: "Contact",
    blog: "Actualités",
    "apply now": "Être certifié",
    "who are we": "Qui sommes-nous ?",
    "company structure": "Structure de l’entreprise",
    "vision mission and core values":
      "Vision, mission et valeurs fondamentales",
    "international approval": "Reconnaissance internationale",
    "what does halal mean": "Que signifie halal ?",
    locations: "Implantations",
    "halal certification process": "Processus de certification halal",
    "product categories": "Catégories de produits",
    "our services": "Nos services",
    "auditing and training": "Audit et formation",
    "benefits of our certification": "Avantages de notre certification",
    "bpjph recognition": "Reconnaissance BPJPH",
    "halal guarantee": "Garantie halal",
    "integrity policy": "Politique d’intégrité",
    "fee regulation": "Règlement tarifaire",
    "confidentiality and impartiality": "Confidentialité et impartialité",
    "rights and duties of applicants": "Droits et devoirs des demandeurs",
    "indonesian regulation": "Réglementation indonésienne",
    "information request": "Demande d’informations",
    feedback: "Avis",
    complaint: "Réclamation",
    "halal certificate check": "Vérifier un certificat halal",
    "saudi application": "Demande pour l’Arabie saoudite",
    address: "ADRESSE",
    phone: "Téléphone",
    email: "E-mail",
    scope: "CHAMP D’APPLICATION",
    "halal inspection and certification": "Inspection et certification halal",
    "useful links": "Liens utiles",
    "contact us": "Nous contacter",
    apply: "Faire une demande",
    "meaning of halal": "Signification du halal",
    "all rights reserved": "Tous droits réservés",
    "powered by": "Développé par",
    "read more": "Lire la suite",
    language: "Langue",
    "languages available": "Langues disponibles",
    loading: "Chargement…",
    "download pdf": "Télécharger le PDF",
    "latest news": "Dernières actualités",
    "more news": "Plus d’actualités",
    "search blog posts": "Rechercher dans les actualités",
    previous: "Précédent",
    next: "Suivant",
    of: "sur",
    "no search results":
      "Aucun résultat. Essayez d’autres mots-clés ou revenez plus tard.",
    "no blog posts":
      "Aucune actualité n’est disponible pour le moment. Revenez bientôt !",
    "accreditation and partnership": "Accréditations et partenariats",
  },
  en: {
    home: "Home",
    "about us": "About us",
    certification: "Certification",
    policies: "Policies",
    contact: "Contact",
    blog: "Blog",
    "apply now": "Apply now",
    "who are we": "Who are we?",
    "company structure": "Company structure",
    "vision mission and core values": "Vision, mission and core values",
    "international approval": "International approval",
    "what does halal mean": "What does halal mean?",
    locations: "Locations",
    "halal certification process": "Halal Certification Process",
    "product categories": "Product categories",
    "our services": "Our services",
    "auditing and training": "Auditing and training",
    "benefits of our certification": "Benefits of our certification",
    "bpjph recognition": "BPJPH recognition",
    "halal guarantee": "Halal guarantee",
    "integrity policy": "Integrity policy",
    "fee regulation": "Fee Regulation",
    "confidentiality and impartiality": "Confidentiality and Impartiality",
    "rights and duties of applicants": "Rights and Duties of applicants",
    "indonesian regulation": "Indonesian Regulation",
    "information request": "Request for information",
    feedback: "Feedback",
    complaint: "Complaint",
    "halal certificate check": "Halal certificate check",
    "saudi application": "Saudi Application",
    address: "ADDRESS",
    phone: "Phone",
    email: "Email",
    scope: "SCOPE",
    "halal inspection and certification": "Halal inspection and certification",
    "useful links": "Useful Links",
    "contact us": "Contact us",
    apply: "Apply",
    "meaning of halal": "Meaning of halal",
    "all rights reserved": "All Rights Reserved",
    "powered by": "Powered by",
    "read more": "Read more",
    language: "Language",
    "languages available": "Languages available",
    loading: "Loading…",
    "download pdf": "Download PDF",
    "latest news": "Latest News",
    "more news": "More News",
    "search blog posts": "Search Blog Posts",
    previous: "Previous",
    next: "Next",
    of: "of",
    "no search results":
      "No results found. Try different keywords or check back later.",
    "no blog posts": "There are no blog posts yet. Stay tuned for updates!",
    "accreditation and partnership": "Accreditation and partnerships",
  },
}

export const getLanguageFromPath = pathname => {
  const segment = pathname?.split("/").filter(Boolean)[0]
  return SUPPORTED_LANGUAGES.includes(segment) ? segment : DEFAULT_LANGUAGE
}

export const stripLanguageFromPath = pathname => {
  if (!pathname) return "/"
  const stripped = pathname.replace(/^\/(?:fr|en)(?=\/|$)/, "")
  return stripped || "/"
}

export const localizePath = (to, language) => {
  if (typeof to !== "string" || !to || !to.startsWith("/")) return to
  if (/^\/(?:admin|login|register|check-certificate|404)(?:\/|$)/.test(to)) {
    return to
  }

  const path = stripLanguageFromPath(to)
  return `/${language}${path === "/" ? "" : path}`
}

export const TranslationProvider = ({ children, pathname }) => {
  const language = getLanguageFromPath(pathname)
  const t = key => translations[language]?.[key] || translations.en[key] || key

  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  return (
    <TranslationContext.Provider value={{ language, lang: language, t }}>
      {children}
    </TranslationContext.Provider>
  )
}

export const useTranslation = pathname => {
  const context = useContext(TranslationContext)
  if (context) return context

  const language = getLanguageFromPath(pathname)
  return {
    language,
    lang: language,
    t: key => translations[language]?.[key] || translations.en[key] || key,
  }
}

export const Link = ({ to, ...props }) => {
  const { language } = useTranslation(
    typeof window !== "undefined" ? window.location.pathname : undefined
  )
  const target =
    typeof to === "string" && /^\/(?:fr|en)(?:\/|$)/.test(to)
      ? to
      : localizePath(to, language)
  return <GatsbyLink to={target} {...props} />
}
